import { Component, inject, signal, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { CardModule } from 'primeng/card';
import { SelectModule } from 'primeng/select';
import { TooltipModule } from 'primeng/tooltip';
import { MessageModule } from 'primeng/message';
import { MiddlewareApi } from '../../core/services/middleware-api.service';
import { ProductsTab } from '../merchants/products-tab';

// Top-level Products page. Reads the event-sourced Catalog module (per-location projection) via the
// shared ProductsTab component. Because the catalog is location-scoped, a merchant must be picked
// first: pick one (or deep-link with ?locationId=X) and ProductsTab owns the list + detail rendering.
@Component({
    selector: 'app-products',
    standalone: true,
    imports: [CardModule, SelectModule, MessageModule, TooltipModule, FormsModule, ProductsTab],
    template: `
        <p-card>
            <ng-template pTemplate="title"><span>Products</span><i class="pi pi-info-circle text-surface-400 ml-2 cursor-help" pTooltip="The online catalog. To see what the POS itself holds, use Administration → XT Items." tooltipPosition="top"></i></ng-template>
            <!-- THE TWO ITEM SURFACES ARE DIFFERENT THINGS AND OPERATORS CONFLATE THEM. This page is what the
                 platform holds: events we ingested and projected. XT Items is the replica of what the POS
                 itself says. They disagree in normal operation — the replica lags — so a price that looks
                 wrong here is answered by comparing the two, and knowing which side you are on is the whole
                 point of saying so on both pages rather than only on the newer one. -->

            <div class="flex flex-wrap items-center gap-2 mb-3">
                <p-select [options]="merchantOptions()" [(ngModel)]="locationId" (onChange)="onSelect()"
                          placeholder="Select a merchant" [filter]="true" styleClass="w-72" />
            </div>

            @if (activeLocation(); as loc) {
                <app-products-tab [locationId]="loc" />
            } @else if (loadError()) {
                <p-message severity="error">{{ loadError() }}</p-message>
            } @else {
                <p class="text-surface-500 dark:text-surface-400">Select a merchant to view its catalog.</p>
            }
        </p-card>
    `
})
export class Products implements OnInit {
    private readonly api = inject(MiddlewareApi);
    private readonly route = inject(ActivatedRoute);

    // The chosen merchant (a location). null until picked — nothing loads before that.
    locationId: string | null = null;
    // The location actually mounted into ProductsTab. Kept separate so a merchant switch can remount
    // the tab (its [lazy] table re-fires onLazyLoad on render — the input change alone would not reload).
    readonly activeLocation = signal<string | null>(null);
    readonly merchantOptions = signal<{ label: string; value: string }[]>([]);
    readonly loadError = signal<string | null>(null);

    ngOnInit(): void {
        // Deep-link support: /products?locationId=X pre-selects that merchant (used by the
        // "View catalog" links on the merchant management page).
        this.locationId = this.route.snapshot.queryParamMap.get('locationId');
        this.activeLocation.set(this.locationId);
        // Merchants = onboarded locations (never Magento instances).
        this.api.getLocations().subscribe({
            next: (r) => this.merchantOptions.set(
                // Label by merchant code, not the opaque location id. Fall back through the store code and
                // name so the picker never regresses to a bare guid; the value stays the location id, which
                // is what the catalog endpoints key on.
                (r.locations ?? []).map((l) => ({
                    label: l.merchantCode || l.magentoCode || l.name || l.locationId,
                    value: l.locationId
                }))
            ),
            error: (e) => this.loadError.set(this.describe(e))
        });
    }

    onSelect(): void {
        // Remount ProductsTab so its lazy table re-fires onLazyLoad and loads the new merchant.
        this.activeLocation.set(null);
        setTimeout(() => this.activeLocation.set(this.locationId));
    }

    private describe(e: any): string {
        if (e?.status === 0) return 'Cannot reach the API.';
        if (e?.status === 403) return 'Forbidden — your role lacks the merchants:read permission.';
        return `Error ${e?.status ?? ''}: ${e?.message ?? 'request failed'}`;
    }
}
